import { FabSearchParams } from '@/modules/fabService/types';
import { captureError } from '@/utils/sentry';
import { runtimeEnv } from '@/lib/runtimeEnv';
import { getExternalBlob, getExternalJson } from '@/infrastructure/clients/externalContentApi';

export interface FabHttpClient {
  get<T>(url: string, params?: Record<string, unknown>): Promise<T>;
  getBlob(url: string): Promise<Blob>;
}

export class FabAdapter implements FabHttpClient {
  private readonly apiBaseUrl: string;

  constructor(apiBaseUrl?: string) {
    // Default to Sketchfab API v3 if no URL is provided
    this.apiBaseUrl = apiBaseUrl || (runtimeEnv.VITE_FAB_API_URL || process.env.VITE_FAB_API_URL || 'https://api.sketchfab.com/v3');
    // The API key is added server-side by getExternalContent
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
      }

      const fullUrl = `${this.apiBaseUrl}${url}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const { data, error } = await getExternalJson<T>(fullUrl);
      if (error) {
        console.error('FabAdapter error response:', { status: error.status, code: error.code, title: error.title, url: fullUrl });
        const httpError = new Error(`HTTP ${error.status}: ${error.title}`);
        captureError(httpError, { domain: 'fab-api', operation: 'get', url, status: error.status, errorText: error.title, errorData: { code: error.code, traceId: error.traceId } });
        throw httpError;
      }

      return data;
    } catch (error) {
      captureError(error as Error, { domain: 'fab-api', operation: 'get', url });
      throw error;
    }
  }

  async getBlob(url: string): Promise<Blob> {
    try {
      const isExternalUrl = url.startsWith('http');
      const fullUrl = isExternalUrl ? url : `${this.apiBaseUrl}${url}`;

      // For external URLs (like S3 pre-signed URLs), they already have auth in query params
      // For internal API URLs, getExternalContent injects the key server-side
      if (isExternalUrl) {
        // External URLs don't need proxy - they have their own auth
        const response = await fetch(fullUrl, {
          method: 'GET',
        });

        if (!response.ok) {
          const errorText = await response.text();
          const error = new Error(`HTTP ${response.status}: ${errorText}`);
          captureError(error, { domain: 'fab-api', operation: 'getBlob', url, status: response.status });
          throw error;
        }

        return response.blob();
      }

      const { data, error } = await getExternalBlob(fullUrl);
      if (error) {
        const httpError = new Error(`HTTP ${error.status}: ${error.title}`);
        captureError(httpError, { domain: 'fab-api', operation: 'getBlob', url, status: error.status });
        throw httpError;
      }

      return data;
    } catch (error) {
      captureError(error as Error, { domain: 'fab-api', operation: 'getBlob', url });
      throw error;
    }
  }

  // Helper method to build search query from params
  buildSearchQuery(params: FabSearchParams): Record<string, unknown> {
    const query: Record<string, unknown> = {};

    // Sketchfab API uses 'q' for search query
    // Ensure query is trimmed and not empty before adding
    if (params.query && params.query.trim()) {
      query.q = params.query.trim();
    }
    
    // IMPORTANT: Only show downloadable models
    query.downloadable = true;
    
    // Sketchfab uses 'categories' (plural) and expects category slugs
    // For now, we'll skip category filtering or map it properly
    // if (params.category) query.categories = params.category;
    
    // Sketchfab doesn't support price filtering in search
    // if (params.priceMin !== undefined) query.price_min = params.priceMin;
    // if (params.priceMax !== undefined) query.price_max = params.priceMax;
    
    // Sketchfab uses 'license' parameter
    if (params.license) query.license = params.license;
    
    // Sketchfab supports rigged and animated filters
    if (params.rigged !== undefined) query.rigged = params.rigged;
    if (params.animated !== undefined) query.animated = params.animated;
    
    // Sketchfab doesn't use file_format in search
    // if (params.fileFormat) query.file_format = params.fileFormat;
    
    // Sketchfab uses cursor-based pagination with 'count' and 'cursor' parameters
    if (params.limit) query.count = Math.min(params.limit, 24); // Sketchfab caps at 24
    
    // Use cursor for pagination if provided
    if (params.cursor) query.cursor = params.cursor;
    
    // Sketchfab face_count range filter (server-side polygon filtering)
    if (params.maxFaceCount !== undefined) {
      query.face_count = `0,${params.maxFaceCount}`;
    }

    // Sketchfab sort options: 'relevance', 'liked', 'viewCount', 'publishedAt'
    if (params.sortBy) {
      const sortMap: Record<string, string> = {
        'relevance': 'relevance',
        'popularity': 'liked',
        'date': 'publishedAt',
      };
      query.sort_by = sortMap[params.sortBy] || params.sortBy;
    }

    return query;
  }
}
